import { FocusManager } from "@lightningjs/ui-components"
import EpgChannel from "../epg-channel"
import EPGConfig from "../epg-config"
import EPGUtility from "../epg-utility"


export default class EPGRowUI extends FocusManager<FocusManager.TemplateSpec> {
    [x: string]: any

    isLoaded = false
    _fetchRegistered = false
    _lastNavTime = 0
    _navThrottleMs = 300
    _tileWindowBatchHours = 4
    _isRowActive = false
    _pendingTileCreate = false

    _canNavigate(): boolean {
        const now = Date.now()
        if (now - this._lastNavTime < this._navThrottleMs) return false
        this._lastNavTime = now
        return true
    }
    static get __componentName(): string {
        return 'EPGRowUI'
    }

    static get tags() {
        return [...super.tags, 'Channel']
    }

    static get properties() {
        return [...super.properties, 'title', 'fixed', 'channelIndex', 'epg', 'channel', 'timerange', 'focusTime']
    }

    override _construct() {
        this.autoResizeHeight = false;
        super._construct();
    }

    _update() {
        super._update()
        this._syncItemsBounds()
        this._updateChannel()
    }

    override _active() {
        this._isRowActive = true
        if (this._pendingTileCreate && this._channel) {
            this._pendingTileCreate = false
            this._doCreateTiles()
        }
    }

    override _inactive() {
        this._isRowActive = false
    }

    override _focus() {
        super._focus()
        ;(this as any).fireAncestors('$requestRowSync', this)
    }

    _doCreateTiles() {
        const channel = this._channel as EpgChannel
        const windowEnd = new Date(this._focusTime || this._timerange.startDate)
        windowEnd.setHours(windowEnd.getHours() + this._tileWindowBatchHours)
        this.items = channel.createPrograms(this._timerange, this._channelIndex, undefined, windowEnd)
        this.selectedIndex = this._getProgramUIIndex()
        this.isLoaded = true
        this._syncItemsBounds()
    }

    _syncItemsBounds(): void {
        const range = this._timerange
        if (!this._Items || !range?.startDate || !range?.endDate) return
        const hours = (range.endDate.getTime() - range.startDate.getTime()) / 3600000
        this._Items.w = hours * EPGConfig.pixelPerHour
        this._Items.h = this.h
    }

    _updateChannel() {
        if (this.isLoaded) {
            if (!this.hasFocus()) {
                this.selectedIndex = this._getProgramUIIndex()
            }
        } else if (this._channel && !this._fetchRegistered) {
            this._fetchRegistered = true
            const channel = this._channel as EpgChannel
            channel.fetchPromise.promise.then(() => {
                if (this._isRowActive) {
                    this._doCreateTiles()
                } else {
                    this._pendingTileCreate = true
                }
            })
        }
    }

    trimOffscreenLeft(viewportLeftEdge: number): void {
        if (!this.isLoaded || !this.items?.length) return
        const firstTile = this.items[0] as any
        const BUFFER_PX = EPGConfig.pixelPerHour
        if (!firstTile || firstTile.x + firstTile.w >= viewportLeftEdge - BUFFER_PX) return

        let trimCount = 0
        while (trimCount < this.items.length - 1) {
            const tile = this.items[trimCount] as any
            if (tile.x + tile.w < viewportLeftEdge - BUFFER_PX) trimCount++
            else break
        }
        for (let i = 0; i < trimCount; i++) {
            this.removeItemAt(0)
        }
    }

    loadMoreProgramsLeft(viewportLeftEdge: number): void {
        if (!this.isLoaded || !this.items?.length) return
        const channel = this._channel as EpgChannel
        const timerange = this._timerange
        if (!channel || !timerange?.startDate) return
        const firstTile = this.items[0] as any
        if (!firstTile || firstTile.x <= viewportLeftEdge) return

        const windowEnd: Date = new Date(firstTile.st)
        const windowStart: Date = timerange.startDate
        if (windowStart >= windowEnd) return

        const newTiles = channel.createPrograms(timerange, this._channelIndex, windowStart, windowEnd)
        if (newTiles.length === 0) return

        this.prependItems(newTiles)
    }

    _loadMorePrograms(): boolean {
        const channel = this._channel as EpgChannel
        if (!channel || !this.items?.length) return false

        const lastItem = this.items[this.items.length - 1] as any
        const windowStart: Date = lastItem.et
        if (!windowStart || windowStart >= this._timerange.endDate) return false

        const windowEnd = new Date(windowStart)
        windowEnd.setHours(windowEnd.getHours() + this._tileWindowBatchHours)

        const newTiles = channel.createPrograms(this._timerange, this._channelIndex, windowStart, windowEnd)
        if (newTiles.length === 0) return false

        this.appendItems(newTiles)
        return true
    }

    extendTilesIfNeeded(): void {
        if (!this.isLoaded || !this.items?.length || !this._focusTime) return
        const lastItem = this.items[this.items.length - 1] as any
        if (!lastItem?.et) return
        const PRELOAD_AHEAD_MS = 2 * 60 * 60 * 1000
        if (this._focusTime.getTime() >= lastItem.et.getTime() - PRELOAD_AHEAD_MS) {
            this._loadMorePrograms()
        }
    }

    override onRight() {
        if (!this._canNavigate()) return true
        if (this.selectedIndex < this.items.length - 1) {
            this.selectNext(false)
            this.fireAncestors('$syncTimelineToProgram', this.selected.st)
            this.fireAncestors('$epgProgramSelected', this.selected)
            return true
        }
        if (this._loadMorePrograms()) {
            this.selectNext(false)
            this.fireAncestors('$syncTimelineToProgram', this.selected.st)
            this.fireAncestors('$epgProgramSelected', this.selected)
            return true
        }
        return false
    }

    override onLeft() {
        if (!this._canNavigate()) return true
        if (this.selectedIndex > 0) {
            ;(this as any).shouldSmooth = false
            this.selectPrevious()
            this.fireAncestors('$syncTimelineToProgram', this.selected.st)
            this.fireAncestors('$epgProgramSelected', this.selected)
            return true
        }
        if (this._loadTrimmedProgramsLeft()) {
            ;(this as any).shouldSmooth = false
            this.selectPrevious()
            this.fireAncestors('$syncTimelineToProgram', this.selected.st)
            this.fireAncestors('$epgProgramSelected', this.selected)
            return true
        }
        ;(this as any).fireAncestors('$focusChannelIcon')
        return true
    }

    _loadTrimmedProgramsLeft(): boolean {
        const channel = this._channel as EpgChannel
        const timerange = this._timerange
        if (!channel || !timerange?.startDate || !this.items?.length) return false
        const firstTile = this.items[0] as any
        if (!firstTile?.st) return false
        const windowStart: Date = timerange.startDate
        const windowEnd: Date = new Date(firstTile.st)
        if (windowStart >= windowEnd) return false
        const newTiles = channel.createPrograms(timerange, this._channelIndex, windowStart, windowEnd)
        if (newTiles.length === 0) return false
        this.prependItems(newTiles)
        return true
    }

    _getProgramUIIndex() {
        return this.items.findIndex(program => EPGUtility.isPlaying(program.st, program.et, this._focusTime))
    }

}
